import React, { useEffect, useRef, useState } from "react";
import "../../_css/InlineMarksEditor.css";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import { notifyError, notifySuccess } from "../helper/Toaster";
import { updateaiEval } from "../../requests";

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4 20h4l10.5-10.5a1.5 1.5 0 0 0 0-2.12L14.62 3.5a1.5 1.5 0 0 0-2.12 0L4 12v8z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M13.5 6.5l4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const InlineMarksEditor = ({
  questionId,
  aiMarks,
  maxMarks,
  remarks = "",
  setRefresh,
}) => {
  const dispatch = useDispatch();
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [override, setOverride] = useState("");
  const [teacherRemarks, setTeacherRemarks] = useState(remarks || "");
  const [saving, setSaving] = useState(false);

  const displayAi = Number(aiMarks);
  const displayMax = Number(maxMarks);

  useEffect(() => {
    if (open) {
      setOverride("");
      setTeacherRemarks(remarks || "");
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, questionId, remarks]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  const handleWheel = (e) => e.preventDefault();

  const handleSave = async () => {
    const value = override === "" ? displayAi : Number(override);
    if (value > displayMax) {
      notifyError("Please enter marks less than maximum marks");
      return;
    }

    setSaving(true);
    dispatch(startLoader());
    try {
      const res = await updateaiEval({
        id: questionId,
        obt_marks: override === "" ? displayAi : override,
        remarks: teacherRemarks,
      });

      if (res.status < 400) {
        notifySuccess("Marks updated");
        setRefresh((p) => !p);
        setOpen(false);
      }
    } catch (error) {
      notifyError("Could not save marks. Try again.");
    } finally {
      setSaving(false);
      dispatch(stopLoader());
    }
  };

  return (
    <div className="inline-marks-wrap" ref={wrapRef}>
      <div className={`inline-marks-display ${open ? "is-open" : ""}`}>
        <span className="inline-marks-score">
          {displayAi}
          <span> / {displayMax}</span>
        </span>
        <button
          type="button"
          className="inline-marks-edit-btn"
          onClick={() => setOpen((o) => !o)}
          aria-label={open ? "Close edit marks" : "Edit marks"}
          aria-expanded={open}
        >
          <EditIcon />
        </button>
      </div>

      {open && (
        <div className="inline-marks-popover" role="dialog" aria-label="Edit marks">
          <p className="inline-marks-popover-title">Edit marks</p>

          <div className="inline-marks-fields-row">
            <div className="inline-marks-field">
              <label>AI marks</label>
              <div className="inline-marks-field-readonly">
                {displayAi} / {displayMax}
              </div>
            </div>

            <div className="inline-marks-field">
              <label htmlFor={`override-${questionId}`}>Teacher override</label>
              <input
                id={`override-${questionId}`}
                ref={inputRef}
                type="number"
                min={0}
                max={displayMax}
                className="inline-marks-field-input no-spinner"
                placeholder={String(displayAi)}
                value={override}
                onChange={(e) => setOverride(e.target.value)}
                onFocus={() => {
                  inputRef.current?.addEventListener("wheel", handleWheel, {
                    passive: false,
                  });
                }}
                onBlur={() => {
                  inputRef.current?.removeEventListener("wheel", handleWheel);
                }}
              />
            </div>
          </div>

          <div className="inline-marks-field">
            <label htmlFor={`teacher-remarks-${questionId}`}>Teacher remarks</label>
            <input
              id={`teacher-remarks-${questionId}`}
              type="text"
              className="inline-marks-field-input"
              placeholder="Add remarks"
              value={teacherRemarks}
              onChange={(e) => setTeacherRemarks(e.target.value)}
            />
          </div>

          <div className="inline-marks-actions">
            <button
              type="button"
              className="inline-marks-btn-cancel"
              onClick={() => setOpen(false)}
            >
              Cancel
            </button>
            <button
              type="button"
              className="inline-marks-btn-save"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default InlineMarksEditor;
